// Delegate SPOC Head (global) access — compact panel for the user edit form.

import React, { useCallback, useEffect, useState } from "react";
import { apiFetch, useApp } from "../../context/AppContext";
import { Button, Input } from "./UI";
import { confirmAction } from "../../utils/sweetAlert";
import { CalendarRange, Shield, Trash2, UserPlus } from "lucide-react";

function formatDate(value) {
  if (!value) return "—";
  try {
    return new Date(`${value}T00:00:00`).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return String(value);
  }
}

function statusStyles(status) {
  if (status === "active") return "bg-emerald-50 text-emerald-700 ring-emerald-200";
  if (status === "scheduled") return "bg-sky-50 text-sky-700 ring-sky-200";
  if (status === "expired") return "bg-amber-50 text-amber-700 ring-amber-200";
  return "bg-slate-100 text-slate-500 ring-slate-200";
}

export default function DelegateAccessSection({
  /** When true, the current viewer may assign/revoke (SPOC Head or admin). */
  canManage = true,
}) {
  const { showNotification } = useApp();
  const [candidates, setCandidates] = useState([]);
  const [delegates, setDelegates] = useState([]);
  const [selected, setSelected] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [bootstrapping, setBootstrapping] = useState(true);

  const loadData = useCallback(async () => {
    try {
      const [candRes, membersRes] = await Promise.all([
        apiFetch("/head-department/candidates"),
        apiFetch("/head-department/members"),
      ]);
      if (candRes.ok) {
        const list = await candRes.json();
        setCandidates(Array.isArray(list) ? list : []);
      }
      if (membersRes.ok) {
        const list = await membersRes.json();
        const open = (Array.isArray(list) ? list : []).filter(
          (d) => d.status === "active" || d.status === "scheduled",
        );
        setDelegates(open);
      }
    } catch {
      showNotification?.("Could not load delegate list", "error");
    } finally {
      setBootstrapping(false);
    }
  }, [showNotification]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleAssign = async () => {
    if (!selected || !startDate || !endDate) {
      showNotification?.("Select a SPOC and both dates", "error");
      return;
    }
    if (endDate < startDate) {
      showNotification?.("End date must be on or after start date", "error");
      return;
    }
    setLoading(true);
    try {
      const res = await apiFetch("/head-department/members", {
        method: "POST",
        body: JSON.stringify({
          user_id: selected,
          start_date: startDate,
          end_date: endDate,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        showNotification?.(data.detail || "Failed to assign delegate", "error");
        return;
      }
      showNotification?.("Delegate access granted");
      setSelected("");
      setStartDate("");
      setEndDate("");
      await loadData();
    } catch {
      showNotification?.("Network error", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleRevoke = async (memberId) => {
    if (!(await confirmAction({
      title: "Revoke delegate access?",
      text: "Revoke this delegate’s global access now?",
      confirmText: "Revoke",
    }))) return;
    setBusyId(memberId);
    try {
      const res = await apiFetch(`/head-department/members/${memberId}`, {
        method: "DELETE",
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        showNotification?.(data.detail || "Failed to revoke", "error");
        return;
      }
      showNotification?.("Delegate revoked");
      await loadData();
    } catch {
      showNotification?.("Network error", "error");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="rounded-xl border border-indigo-100 bg-gradient-to-br from-indigo-50/60 to-white p-4 space-y-3">
      <div className="flex items-start gap-2.5">
        <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-indigo-100 text-indigo-700">
          <Shield size={16} />
        </div>
        <div className="min-w-0">
          <p className="text-sm font-semibold text-slate-900">
            Delegate global access
          </p>
          <p className="text-xs text-slate-500 leading-relaxed mt-0.5">
            Temporarily share SPOC Head access with another department SPOC.
            Their home department and role stay the same; access ends
            automatically after the end date.
          </p>
        </div>
      </div>

      {bootstrapping ? (
        <p className="text-xs text-slate-400 pl-10">Loading…</p>
      ) : (
        <>
          {delegates.length > 0 ? (
            <ul className="space-y-2">
              {delegates.map((d) => (
                <li
                  key={d.id}
                  className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 bg-white px-3 py-2"
                >
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-slate-800 truncate">
                      {d.user_name || d.user_email}
                    </p>
                    <p className="text-[11px] text-slate-500 flex items-center gap-1 mt-0.5">
                      <CalendarRange size={11} />
                      {formatDate(d.start_date)} → {formatDate(d.end_date)}
                      {d.home_department_name
                        ? ` · ${d.home_department_name}`
                        : ""}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span
                      className={`text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full ring-1 ${statusStyles(d.status)}`}
                    >
                      {d.status}
                    </span>
                    {canManage && (
                      <button
                        type="button"
                        disabled={busyId === d.id}
                        onClick={() => handleRevoke(d.id)}
                        className="p-1.5 rounded-lg text-red-600 hover:bg-red-50 disabled:opacity-50"
                        title="Revoke"
                      >
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-xs text-slate-400 pl-1">
              No active or scheduled delegates.
            </p>
          )}

          {/* Only one delegate may hold global access at a time */}
          {canManage && delegates.length > 0 && (
            <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-100 rounded-lg px-2.5 py-2 leading-relaxed">
              Only one delegate can hold global access at a time. Revoke the
              current delegate above before assigning someone else.
            </p>
          )}

          {canManage && delegates.length === 0 && (
            <div className="rounded-lg border border-dashed border-indigo-200 bg-white/80 p-3 space-y-2.5">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-indigo-600 flex items-center gap-1.5">
                <UserPlus size={12} />
                Assign delegate
              </p>
              <select
                className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                value={selected}
                onChange={(e) => setSelected(e.target.value)}
              >
                <option value="">Select an active SPOC…</option>
                {candidates.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.full_name}
                    {c.department_name ? ` · ${c.department_name}` : ""}
                  </option>
                ))}
              </select>
              {candidates.length === 0 && (
                <p className="text-[11px] text-slate-400">
                  No other department SPOCs available to delegate to.
                </p>
              )}
              <div className="grid grid-cols-2 gap-2">
                <Input
                  type="date"
                  label="Start"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
                <Input
                  type="date"
                  label="End"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </div>
              <Button
                size="sm"
                loading={loading}
                disabled={!selected || !startDate || !endDate}
                onClick={handleAssign}
                leftIcon={<UserPlus size={14} />}
              >
                Grant access
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
